import React, { useMemo, useState } from 'react';
import { PlusIcon, XIcon } from 'lucide-react';
import { BUTTON_QUIET, FIELD, OPTION, SECTION_HINT, SECTION_TITLE, SELECT } from './adminStyles';
import type { CallSheetCrewMember, StaffOption } from '../../utils/callSheets';

/**
 * The crew on a call sheet (13 Sep 2026).
 *
 * Two kinds of person sit in this one list, and the difference is worth
 * showing rather than hiding:
 *
 *   • inherited — the attached project's team. Read-only here, because the
 *     project's Team is where that belongs and two places to edit one fact is
 *     how they drift apart. Only the phone number is editable, and that saves
 *     against the person, not the sheet.
 *   • picked — chosen for this day. A standalone call sheet has only these,
 *     and an attached one can add anyone the project team doesn't cover.
 *
 * Freelancers and one-off people without a Portal OS account go in Extra
 * Crew below this, unchanged.
 */
interface CallSheetCrewProps {
  crew: CallSheetCrewMember[];
  options: StaffOption[];
  hasProject: boolean;
  projectName: string | null;
  onAdd: (userId: string) => void;
  onRemove: (userId: string) => void;
  onRoleChange: (userId: string, role: string) => void;
  onPhoneChange: (userId: string, phone: string) => void;
  onPhoneBlur: (userId: string, phone: string) => void;
}
export function CallSheetCrew({
  crew,
  options,
  hasProject,
  projectName,
  onAdd,
  onRemove,
  onRoleChange,
  onPhoneChange,
  onPhoneBlur
}: CallSheetCrewProps) {
  const [picking, setPicking] = useState('');
  const available = useMemo(() => options.filter(option => !crew.some(member => member.userId === option.userId)), [options, crew]);
  return <section className="mt-14 border-t border-ruleDark pt-8" data-id="element-615" data-mp="2tvoqaom5h0h7">
      <h2 className={SECTION_TITLE} data-id="element-616" data-mp="2tu3yh6r7zvn1">Crew</h2>
      <p className={SECTION_HINT} data-id="element-617" data-mp="13kk5ri93xnf9">
        {hasProject ? `Everyone on ${projectName ?? 'the attached project'}’s team is here automatically — add or remove them on the project. Anyone you add below is on this day only.` : 'Pick who’s on this day. Phone numbers save against the person, so every future call sheet has them.'}
      </p>

      {crew.length === 0 ? <p className="mt-6 font-display text-lg text-paper/40" data-id="element-618" data-mp="38kliyw250yhp">Nobody on this call sheet yet.</p> : <ul className="mt-6 divide-y divide-paper/10 border-t border-paper/10" data-id="element-619" data-mp="3dkzghk644q1h">
          {crew.map(member => <li key={member.userId} className="flex flex-wrap items-center gap-4 py-4" data-id="element-620" data-mp="3hkrpfxzf4y7d">
              <span className="min-w-[9rem] flex-1 font-sans text-sm text-paper" data-id="element-621" data-mp="3mjky9d3rg117">
                {member.name}
                {member.fromProject && <span className="label-sm ml-3 text-paper/25" data-id="element-622" data-mp="51ro2l11q5a">from project</span>}
              </span>

              {member.fromProject ? <span className="min-w-[8rem] font-sans text-sm text-paper/35" data-id="element-623" data-mp="55pjurkzsd13">
                  {member.role || '—'}
                </span> : <input value={member.role ?? ''} onChange={event => onRoleChange(member.userId, event.target.value)} placeholder="Role on the day" className={`${FIELD} mt-0 max-w-[12rem]`} data-id="element-624" data-mp="1to0qsvfh7qmi" />}

              <input value={member.phone ?? ''} onChange={event => onPhoneChange(member.userId, event.target.value)} onBlur={event => onPhoneBlur(member.userId, event.target.value)} placeholder="Phone number" className={`${FIELD} mt-0 max-w-[11rem]`} data-id="element-625" data-mp="1foj02aime3ai" />

              {member.fromProject ? <span className="w-4 shrink-0" aria-hidden="true" data-id="element-626" data-mp="1yndcvdyiwto1" /> : <button type="button" onClick={() => onRemove(member.userId)} aria-label={`Remove ${member.name} from this call sheet`} className="shrink-0 text-paper/30 transition-colors duration-150 ease-out hover:text-paper" data-id="element-627" data-mp="2w1zb1ktee4a8">
                  <XIcon className="h-4 w-4" strokeWidth={1.5} data-id="element-628" data-mp="2fvcy22v5qeiy" />
                </button>}
            </li>)}
        </ul>}

      {available.length > 0 && <div className="mt-6 flex flex-wrap items-end gap-5" data-id="element-629" data-mp="26u6h5solj1p6">
          <label className="block min-w-[14rem]" data-id="element-630" data-mp="26wiu1rfhylvd">
            <span className="label block text-paper/40" data-id="element-631" data-mp="2ujqzqagu7wxx">Add someone</span>
            <select value={picking} onChange={event => setPicking(event.target.value)} className={SELECT} data-id="element-632" data-mp="2zj3stcyay08y">
              <option value="" className={OPTION} data-id="element-633" data-mp="2ktxo59gp3hto">
                Choose…
              </option>
              {available.map(option => <option key={option.userId} value={option.userId} className={OPTION} data-id="element-634" data-mp="3dj4tarann5nw">
                  {option.name}
                </option>)}
            </select>
          </label>
          <button type="button" disabled={!picking} onClick={() => {
        onAdd(picking);
        setPicking('');
      }} className={`${BUTTON_QUIET} flex items-center gap-2 pb-2 disabled:opacity-30`} data-id="element-635" data-mp="16ucos2fit7k4">
            <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-636" data-mp="1for4g63yqk3r" />
            Add To Call Sheet
          </button>
        </div>}
    </section>;
}